/**
 * Templates are CSS-variable bundles applied on the shadow host.
 * Each template maps to:
 *   - one variable set (`vars`) injected as inline style,
 *   - one layout key (`layout`) the LayoutManager dispatches on,
 *   - optional `body` classes added to the widget root for layout-mode
 *     targeting (e.g. `ometrics-template-floating-bubble`).
 *
 * Customers don't author templates directly - the admin SPA picks one
 * and the backend serves the resolved variable values via
 * `WidgetConfigResolver`. A custom theme overrides any variable.
 */

const SHARED_DEFAULTS = {
  '--ometrics-bubble-radius': 'var(--radius-widget)',
  '--ometrics-accent': 'var(--color-brand)',
  '--ometrics-bg': 'var(--color-surface)',
  '--ometrics-fg': 'var(--color-surface-foreground)',
  '--ometrics-font':
    "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
}

export const TEMPLATES = {
  panel: {
    layout: 'overlay',
    vars: {
      ...SHARED_DEFAULTS,
      '--ometrics-bubble-radius': '14px',
    },
    bodyClass: 'ometrics-template-panel',
  },
  compact: {
    layout: 'floating-bubble',
    vars: {
      ...SHARED_DEFAULTS,
      '--ometrics-bubble-radius': '20px',
    },
    bodyClass: 'ometrics-template-compact',
  },
  fullscreen: {
    layout: 'fullscreen',
    vars: {
      ...SHARED_DEFAULTS,
      '--ometrics-bubble-radius': '0px',
    },
    bodyClass: 'ometrics-template-fullscreen',
  },
  'floating-bubble': {
    layout: 'floating-bubble',
    vars: {
      ...SHARED_DEFAULTS,
    },
    bodyClass: 'ometrics-template-floating-bubble',
  },
  'slide-out': {
    layout: 'slide-out',
    vars: {
      ...SHARED_DEFAULTS,
      '--ometrics-bubble-radius': '0px',
    },
    bodyClass: 'ometrics-template-slide-out',
  },
  /**
   * In-page surface. Renders a compact trigger (search box, button, or
   * Ometrics bubble icon per `widgetType`) that opens the chat in the
   * configured `displayMode` — overlay, expand, or slide-out. See
   * {@link InjectedLayout}.
   */
  injected: {
    layout: 'injected',
    vars: {
      ...SHARED_DEFAULTS,
    },
    bodyClass: 'ometrics-template-injected',
  },
}

/**
 * Resolve a template descriptor for a given config. Falls back to
 * `panel` when the configured template is unknown or missing.
 */
export function resolveTemplate(conf) {
  // All in-page (injected) surfaces render a trigger that opens the chat in
  // the configured display mode (overlay / expand / slide-out). The single
  // `InjectedLayout` dispatches on `widgetType` + `displayMode`.
  if (conf?.kind === 'injected') {
    return TEMPLATES.injected
  }
  const key =
    conf?.template ||
    conf?.ometricsAI?.template ||
    legacyDisplayModeToTemplate(conf) ||
    'panel'
  return TEMPLATES[key] || TEMPLATES.panel
}

/**
 * Whether an in-page (`injected`) surface is configured to open as a
 * side drawer. Mirrors `canonicalInjectedDisplayMode` in
 * {@link InjectedLayout}; mobile always promotes to slide-out.
 */
function injectedOpensAsSlideOut(conf) {
  if (conf?.isMobile === true) return true
  const raw = String(conf?.displayMode || conf?.ometricsAI?.displayMode || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
  if (raw.includes('slide') && raw.includes('out')) return true
  return raw === 'slideout' || raw === 'mobile'
}

/**
 * Layout key used to decide *suggestion placement* (panel-bottom strip vs
 * inline under the latest bot message). This differs from
 * `resolveTemplate(conf).layout`, which the `LayoutManager` uses to pick a
 * layout component: an in-page surface always renders through
 * `InjectedLayout` (layout key `injected`), but when it opens as a side
 * drawer it should place suggestions exactly like the floating slide-out
 * (`slide-out`). Without this, the in-page slide-out shows a static
 * panel-bottom suggestions strip while the floating slide-out shows them
 * inline.
 */
export function resolveSuggestionsLayoutKey(conf) {
  const base = resolveTemplate(conf).layout
  if (base === 'injected' && injectedOpensAsSlideOut(conf)) {
    return 'slide-out'
  }
  return base
}

/**
 * The layout key `LayoutManager` actually renders with — same precedence
 * as `LayoutManager`: floating search overrides the template, and mobile
 * promotes `slide-out` to `fullscreen` (the slide-out width animates
 * poorly under iOS Safari soft-keyboard transitions). Kept in sync with
 * `LayoutManager` so anything gating behavior on "is this a slide-out
 * surface" (e.g. auto-opening the chat panel for background triggers)
 * agrees with what actually renders.
 */
export function resolveEffectiveLayoutKey(conf) {
  const template = resolveTemplate(conf)
  const isMobile = conf?.isMobile === true
  const isFloatingSearch =
    String(conf?.kind || '').toLowerCase() === 'floating' &&
    String(conf?.widgetType || '').trim().toLowerCase() === 'search'
  if (isFloatingSearch) return 'floating-search'
  if (isMobile && template.layout === 'slide-out') return 'fullscreen'
  return template.layout
}

/**
 * Whether the widget currently renders as a slide-out side drawer —
 * either the dedicated `slide-out` template, or an in-page (`injected`)
 * surface configured (or mobile-forced) to open as one. Used to gate
 * background triggers (upsell, abandoned cart, free shipping) that would
 * otherwise force-open an overlay/expand/floating panel, which is far
 * more intrusive than a drawer sliding in from the edge.
 */
export function isSlideOutSurface(conf) {
  const key = resolveEffectiveLayoutKey(conf)
  if (key === 'slide-out') return true
  if (key === 'injected') return injectedOpensAsSlideOut(conf)
  return false
}

function legacyDisplayModeToTemplate(conf) {
  let mode = String(conf?.displayMode || conf?.ometricsAI?.displayMode || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
  if (mode.includes('slide') && mode.includes('out')) mode = 'slide-out'
  if (mode === 'slideout') mode = 'slide-out'
  if (mode === 'floating' || mode === 'floating-bubble') return 'floating-bubble'
  if (mode === 'slide-out' || mode === 'mobile') return 'slide-out'
  if (mode === 'fullscreen') return 'fullscreen'
  if (mode === 'injected' || mode === 'inline') return 'injected'
  if (mode === 'overlay' || mode === 'expand') return 'panel'
  return null
}

/**
 * Apply a template's CSS variables to a host element. Used by
 * `widget.js` after creating the shadow host.
 */
export function applyTemplateToHost(host, template, themeOverrides = {}) {
  if (!host || !template) return
  const merged = { ...template.vars, ...themeOverrides }
  for (const [name, value] of Object.entries(merged)) {
    if (value == null) continue
    host.style.setProperty(name, String(value))
  }
  if (template.bodyClass) {
    host.classList.add(template.bodyClass)
  }
}
